import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Container from "../../../layouts/Container";
import Modal from "../../../components/Modal";
import SearchBar from "../../../components/SearchBar";
import DeviceForm from "../components/DeviceForm";
import DeviceTable from "../components/DeviceTable";
import ButtonForm from "../../../components/ButtonForm";
import Spinner from "../../../components/Spinner";

import {
  getDevices,
  createDevice,
  updateDevice,
  deleteDevice,
} from "../../../services/apiDevices";
import { useAuth } from "../../../providers/AuthContext";
import { useAppContext } from "../../../providers/AppContext";

function Devices() {
  const {auth} = useAuth();
  const { addToast } = useAppContext();
  const [devices, setDevices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [deviceToEdit, setDeviceToEdit] = useState(null);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [elementsPerPage, setElementsPerPage] = useState(10);
  const pageSizes = [2, 5, 10, 20];
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isAssign, setIsAssign] = useState(false);

  const [sortBy, setSortBy] = useState(
    JSON.stringify({ column: "createdAt", isAscending: true })
  );

  const refreshElements = useCallback(async () => {
    setIsLoading(true);
    try {
      getDevices(
        currentPage,
        elementsPerPage,
        searchTerm,
        sortBy,
        auth?.token
      ).then(data => {
        setDevices(data);
        setTotalElements(data.length);
        setTotalPages(Math.ceil(data.length / elementsPerPage));
      }).catch(error => {
        console.log(error);
        addToast('Sesión expirada' , 3)
        navigate('/logout')
      });
      
    } catch (error) {
      console.error("Error fetching devices:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, elementsPerPage, searchTerm, sortBy]);

  useEffect(() => {
    refreshElements();
  }, [refreshElements]);

  const handleCreateDevice = async (newDevice) => {
    setIsCreating(true);
    try {
      createDevice(newDevice,auth?.token).then(response => {
        addToast('Dispositivo creado exitosamente' , 0)
        setShowForm(false);
        refreshElements();
      }).catch(error => {
          addToast('Sesión expirada' , 3)
          navigate('/logout')
        }
      );
      
    } catch (error) {
      console.error("Error al crear dispositivo:", error);
      console.log(error.status);
      setErrorMessage(
        "Error al crear dispositivo. Por favor, inténtalo de nuevo."
      );
      setShowErrorModal(true);
    } finally {
      setIsCreating(false);
    }
  };

  const handleAssign = (device) => {
    setIsUpdating(true);
    setIsAssign(true);
    setDeviceToEdit({ ...device, isAssign: true });
    setShowForm(true);
    setIsUpdating(false);
  };

  const completeAssign = async (updatedDevice) => {
    setIsUpdating(true);
    try {
      updateDevice(updatedDevice,auth?.token).then(response => {
        setDeviceToEdit(null);
        setIsAssign(!isAssign);
        setShowForm(false);
        refreshElements();
        setSuccessMessage("Organización asignada exitosamente!");
        setShowSuccessModal(true);
      }).catch(error =>
        navigate('/logout')
      );
      
    } catch (error) {
      console.error("Error al asignar Organización:", error);
      setErrorMessage(
        "Error al asignar organización. Por favor, inténtalo de nuevo."
      );
      setShowErrorModal(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = (device) => {
    setDeviceToDelete(device);
    setShowDeleteConfirmation(true);
  };

  const handleCompleteDeleting = async () => {
    setIsDeleting(true);
    try {
      deleteDevice(deviceToDelete, auth?.token).then(response => {
        setDeviceToDelete(null);
        setShowDeleteConfirmation(false);
        refreshElements();
        setSuccessMessage("Dispositivo eliminado exitosamente!");
        setShowSuccessModal(true);
      }).catch(error =>
        navigate('/logout')
      );
      
    } catch (error) {
      console.error("Error al eliminar dispositivo:", error);
      setErrorMessage(
        "Error al eliminar dispositivo. Por favor, inténtalo de nuevo."
      );
      setShowErrorModal(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const startPage =
    currentPage === totalPages
      ? Math.max(1, currentPage - 2)
      : Math.max(1, currentPage - 1);
  const endPage = Math.min(totalPages, startPage + 2);

  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
        setSuccessMessage("");
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  useEffect(() => {
    if (showErrorModal) {
      const timer = setTimeout(() => {
        setShowErrorModal(false);
        setErrorMessage("");
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [showErrorModal]);

  const handleHeaderClick = (column) => {
    const currentSort = JSON.parse(sortBy);
    const newIsAscending =
      currentSort.column === column ? !currentSort.isAscending : true;
    setSortBy(JSON.stringify({ column, isAscending: newIsAscending }));
    setCurrentPage(1);
  };

  const navigate = useNavigate();

  const handleDeviceClick = (device) => {
    navigate(`/device/${device._id}`); 
  };

  return (
    <Container>
      <div className="w-11/12 mx-auto p-4 my-2">
        <div className="flex items-center justify-between my-2">
          <SearchBar
            type="search"
            text="Filtrar por nombre"
            id="search_bar__by_name_devices"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
          />
          <ButtonForm
            text="Crear Dispositivo"
            enabled={true}
            action={() => setShowForm(true)}
          />
        </div>

        <Modal
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setDeviceToEdit(null);
          }}
          title={
            deviceToEdit
              ? `Asignar Organización a " ${deviceToEdit?.name} "`
              : "Crear Dispositivo"
          }
        >
          {isUpdating ? (
            <div className="flex justify-center items-center h-screen">
              <Spinner />
            </div>
          ) : (
            <DeviceForm
              onSubmit={
                deviceToEdit ? completeAssign : handleCreateDevice
              }
              device={deviceToEdit}
            />
          )}
        </Modal>

        {isLoading ? (
          <div className="flex justify-center items-center h-screen">
            <Spinner />
          </div>
        ) : (
          <DeviceTable
            devices={devices}
            onAssign={handleAssign}
            onEdit={handleDeviceClick}
            onDelete={handleDelete}
            onSort={handleHeaderClick}
            sortBy={sortBy}
          />
        )}

        <div className="mt-4 flex justify-between items-center">
          <div className="flex justify-between items-center">
            <p className="mr-2">Total de Dispositivos:</p>
            <span
              className={`text-3xl font-bold transition-all duration-500 text-bluesens`}
            >
              {totalElements}
            </span>
          </div>
          {devices.length > 0 && (
            <div className="flex flex-col justify-center gap-y-2 items-center">
              <span>Navegación entre Páginas</span>
              <div className="flex gap-x-1 items-center border-2 rounded p-2">
                <button
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md border-2 border-bluesens border-solid disabled:opacity-50 disabled:border-none`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/svg/skip_previous.svg`}
                    alt="First Page"
                    className="h-5 w-5"
                  />
                </button>

                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md border-2 border-bluesens border-solid disabled:opacity-50 disabled:border-none`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/svg/arrow_back.svg`}
                    alt="Previous Page"
                    className="h-5 w-5"
                  />
                </button>

                {Array.from({ length: endPage - startPage + 1 }, (_, index) =>
                  index + 1 <= endPage - startPage ||
                  totalPages <= 3 ||
                  totalPages - 1 <= currentPage ? (
                    <button
                      key={startPage + index}
                      onClick={() => paginate(startPage + index)}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === startPage + index
                          ? "bg-bluesens text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      {startPage + index}
                    </button>
                  ) : (
                    <>
                      <span>...</span>
                      <button
                        key={totalPages}
                        onClick={() => paginate(totalPages)}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === startPage + index
                            ? "bg-blue-500 text-white"
                            : "bg-gray-200"
                        }`}
                      >
                        {totalPages}
                      </button>
                    </>
                  )
                )}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-md border-2 border-bluesens border-solid disabled:opacity-50 disabled:border-none`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/svg/arrow_forward.svg`}
                    alt="Next Page"
                    className="h-5 w-5"
                  />
                </button>

                <button
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-md border-2 border-bluesens border-solid disabled:opacity-50 disabled:border-none`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/svg/skip_next.svg`}
                    alt="Last Page"
                    className="h-5 w-5"
                  />
                </button>
              </div>
            </div>
          )}
          {devices.length > 0 && (
            <div>
              <label htmlFor="pageSize" className="mr-2">
                Elementos por Página:
              </label>
              <select
                id="pageSize"
                value={elementsPerPage}
                onChange={(e) => {
                  setElementsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
                className="border rounded-md py-1 px-2"
              >
                {pageSizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <Modal
          isOpen={showDeleteConfirmation}
          title="Confirmar Eliminación"
          showCloseButton={false}
        >
          <p className="mb-4">
            ¿Estás seguro de que quieres eliminar el dispositivo{" "}
            <span className="font-bold text-red-500">
              {deviceToDelete?.name || "Desconocida"}
            </span>
            ?
          </p>
          <div className="flex justify-end">
            <button
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded mr-2"
              onClick={handleCompleteDeleting}
            >
              Eliminar
            </button>
            <button
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              onClick={() => setShowDeleteConfirmation(false)}
            >
              Cancelar
            </button>
          </div>
        </Modal>
      </div>

      {isCreating || isUpdating || isDeleting ? (
        <div className="flex justify-center items-center h-screen">
          <Spinner />
        </div>
      ) : (
        <>
          {/* Success Modal */}
          <Modal
            title="Éxito"
            isOpen={showSuccessModal}
            onClose={() => setShowSuccessModal(false)}
            colorTitle="bg-green-300"
            size="w-fit"
            showCloseButton={false}
          >
            <div className="text-center">
              <p>{successMessage}</p>
            </div>
          </Modal>
          {/* Error Modal */}
          <Modal
            title="Error"
            isOpen={showErrorModal}
            onClose={() => setShowErrorModal(false)}
            colorTitle="bg-red-300"
            size="w-fit"
            showCloseButton={false}
          >
            <div className="text-center">
              <p>{errorMessage}</p>
            </div>
          </Modal>
        </>
      )}
    </Container>
  );
}

export default Devices;
